import { pageBgColor } from "@/utils/theme";
import {
	ButtonWithLoading,
	ErrorModal,
	getMomentTz,
	HelmetDexteel,
} from "@dexteel/mesf-core";
import { DashboardOutlined, TableChartOutlined } from "@mui/icons-material";
import PictureAsPdf from "@mui/icons-material/PictureAsPdf";
import {
	Grid2,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { Moment } from "moment-timezone";
import {
	parseAsArrayOf,
	parseAsBoolean,
	parseAsInteger,
	parseAsString,
	parseAsStringLiteral,
	useQueryStates,
} from "nuqs";
import { useEffect, useState } from "react";
import {
	InspectionReport,
	InspectionReportFilterOptions,
} from "../models/InspectionReport";
import {
	getInspectionDashboardPdf,
	getInspectionReport,
	getInspectionReportFilters,
} from "../repositories/InspectionReportRepository";
import { InspectionsReportDashboard } from "./components/InspectionsReportDashboard";
import { InspectionsReportFilters } from "./components/InspectionsReportFilters";
import { InspectionsReportTable } from "./components/InspectionsReportTable";
import {
	DATE_PRESET_VALUES,
	formatDateRange,
	getInspectionEventLabel,
	getPresetRange,
	hasPresetShape,
	type InspectionDatePreset,
	toDashboardWindow,
} from "./utils/period";

const moment = getMomentTz();

export type InspectionReportFilters = {
	startDate: Moment | null;
	endDate: Moment | null;
	sectionId: number | null;
	eventId: number | null;
	heatId: number | null;
	surfaceDefectIds: number[];
	problemsOnly: boolean;
	datesPinned: boolean;
	datePreset: InspectionDatePreset | null;
	sectionName: string | null;
	heatName: string | null;
	surfaceDefectNames: string[];
};

const REPORT_QUERY = {
	view: parseAsStringLiteral(["table", "dashboard"]).withDefault("table"),
	from: parseAsString,
	to: parseAsString,
	section: parseAsInteger,
	event: parseAsInteger,
	heat: parseAsInteger,
	defects: parseAsArrayOf(parseAsInteger).withDefault([]),
	problems: parseAsBoolean.withDefault(false),
	preset: parseAsStringLiteral(DATE_PRESET_VALUES).withDefault("last7days"),
};

const defaultStartDate = () => getPresetRange("last7days").start;
const defaultEndDate = () => getPresetRange("last7days").end;

const readFilterDate = (
	value: string | null,
	edge: "start" | "end",
	fallback: () => Moment,
): Moment | null => {
	if (value === null) return fallback();
	if (value === "any") return null;

	const parsed = moment(value, "YYYY-MM-DD", true);

	if (!parsed.isValid()) return fallback();

	return edge === "start" ? parsed.startOf("day") : parsed.endOf("day");
};

const NO_LABELS: {
	sectionName: string | null;
	heatName: string | null;
	surfaceDefectNames: string[];
} = {
	sectionName: null,
	heatName: null,
	surfaceDefectNames: [],
};

const toQueryPatch = (updates: Partial<InspectionReportFilters>) => ({
	...("startDate" in updates
		? {
				from: updates.startDate
					? updates.startDate.format("YYYY-MM-DD")
					: "any",
			}
		: {}),
	...("endDate" in updates
		? { to: updates.endDate ? updates.endDate.format("YYYY-MM-DD") : "any" }
		: {}),
	...("sectionId" in updates ? { section: updates.sectionId ?? null } : {}),
	...("eventId" in updates ? { event: updates.eventId ?? null } : {}),
	...("heatId" in updates ? { heat: updates.heatId ?? null } : {}),
	...("surfaceDefectIds" in updates
		? { defects: updates.surfaceDefectIds ?? [] }
		: {}),
	...("problemsOnly" in updates
		? { problems: updates.problemsOnly ?? false }
		: {}),
	...("datePreset" in updates ? { preset: updates.datePreset ?? null } : {}),
});

const narrows = (updates: Partial<InspectionReportFilters>) =>
	(["sectionId", "eventId", "heatId", "surfaceDefectIds"] as const).some(
		(key) => {
			const value = updates[key];

			return Array.isArray(value) ? value.length > 0 : !!value;
		},
	);

const useInspectionReportFilters = ({
	startDate,
	endDate,
	sectionId,
}: {
	startDate: Date | null;
	endDate: Date | null;
	sectionId: number | null;
}) =>
	useQuery<InspectionReportFilterOptions, Error>({
		queryKey: [
			"inspection-report-filters",
			startDate?.getTime() ?? null,
			endDate?.getTime() ?? null,
			sectionId,
		],
		queryFn: ({ signal }) =>
			getInspectionReportFilters(
				{ startDate, endDate, sectionId: sectionId },
				signal,
			),
	});

const useInspectionReport = ({
	startDate,
	endDate,
	sectionId,
	eventId,
	heatId,
	surfaceDefectIds,
	problemsOnly,
	enabled = true,
}: {
	startDate: Date | null;
	endDate: Date | null;
	sectionId: number | null;
	eventId: number | null;
	heatId: number | null;
	surfaceDefectIds: number[];
	problemsOnly: boolean;
	enabled?: boolean;
}) =>
	useQuery<InspectionReport, Error>({
		queryKey: [
			"inspection-report",
			startDate?.getTime() ?? null,
			endDate?.getTime() ?? null,
			sectionId,
			eventId,
			heatId,
			surfaceDefectIds.join(","),
			problemsOnly,
		],
		queryFn: ({ signal }) =>
			getInspectionReport(
				{
					startDate,
					endDate,
					sectionId: sectionId,
					eventId: eventId,
					heatId: heatId,
					surfaceDefectIds,
					problemsOnly,
				},
				signal,
			),
		enabled,
	});

export const InspectionsReportPage = () => {
	const [query, setQuery] = useQueryStates(REPORT_QUERY);

	const [labels, setLabels] = useState<{
		sectionName: string | null;
		heatName: string | null;
		surfaceDefectNames: string[];
	}>(NO_LABELS);
	const [datesPinned, setDatesPinned] = useState(
		() =>
			(query.from !== null || query.to !== null) &&
			!hasPresetShape(
				query.preset,
				readFilterDate(query.from, "start", defaultStartDate),
				readFilterDate(query.to, "end", defaultEndDate),
			),
	);
	const [expandAll, setExpandAll] = useState(false);
	const [error, setError] = useState("");
	const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

	const view = query.view;
	const isDashboard = view === "dashboard";

	const filterStart = readFilterDate(query.from, "start", defaultStartDate);
	const filterEnd = readFilterDate(query.to, "end", defaultEndDate);

	const filters: InspectionReportFilters = {
		startDate: filterStart,
		endDate: filterEnd,
		sectionId: query.section,
		eventId: query.event,
		heatId: query.heat,
		surfaceDefectIds: query.defects,
		problemsOnly: query.problems,
		datesPinned,
		datePreset: hasPresetShape(query.preset, filterStart, filterEnd)
			? query.preset
			: null,
		...labels,
	};

	const startDate = filters.startDate?.toDate() ?? null;
	const endDate = filters.endDate?.toDate() ?? null;

	const {
		data: options,
		isLoading: isLoadingOptions,
		isError: isOptionsError,
		error: optionsError,
	} = useInspectionReportFilters({
		startDate,
		endDate,
		sectionId: filters.sectionId,
	});

	const heatId =
		options && !options.heats.some((heat) => heat.HeatId === filters.heatId)
			? null
			: filters.heatId;

	const eventId =
		options &&
		!options.events.some((event) => event.EventId === filters.eventId)
			? null
			: filters.eventId;

	const heatName = heatId === null ? null : filters.heatName;

	const {
		data: report,
		isLoading,
		isError: isReportError,
		error: reportError,
	} = useInspectionReport({
		startDate,
		endDate,
		sectionId: filters.sectionId,
		eventId: eventId,
		heatId: heatId,
		surfaceDefectIds: filters.surfaceDefectIds,
		problemsOnly: filters.problemsOnly,
		enabled: !isDashboard && options !== undefined,
	});

	const dashboardEventId =
		eventId ??
		(filters.sectionId !== null ? (options?.events[0]?.EventId ?? null) : null);

	const dashboardEvent =
		options?.events.find((event) => event.EventId === dashboardEventId) ?? null;

	const dashboardWindow =
		filters.startDate && filters.endDate
			? toDashboardWindow(filters.startDate, filters.endDate)
			: null;

	const dashboardLabel = dashboardEvent
		? getInspectionEventLabel(dashboardEvent)
		: formatDateRange(filters.startDate, filters.endDate);

	const changedFilters = [
		query.from !== null || query.to !== null,
		query.section !== null,
		query.event !== null,
		query.heat !== null,
		query.defects.length > 0,
		query.problems,
	].filter(Boolean).length;

	const restoreFilters = () => {
		setLabels(NO_LABELS);
		setDatesPinned(false);
		setQuery({
			from: null,
			to: null,
			section: null,
			event: null,
			heat: null,
			defects: null,
			problems: null,
			preset: null,
		});
	};

	const handleFilterChange = (updates: Partial<InspectionReportFilters>) => {
		const touchedDates = "startDate" in updates || "endDate" in updates;
		const clearDates = !datesPinned && !touchedDates && narrows(updates);

		const clearRun = isDashboard && touchedDates;

		setLabels((current) => ({
			sectionName: clearRun
				? null
				: "sectionName" in updates
					? (updates.sectionName ?? null)
					: current.sectionName,
			heatName:
				"heatName" in updates ? (updates.heatName ?? null) : current.heatName,
			surfaceDefectNames:
				updates.surfaceDefectNames ?? current.surfaceDefectNames,
		}));

		if (touchedDates) setDatesPinned(updates.datesPinned ?? true);

		setQuery({
			...toQueryPatch(updates),
			...(clearDates ? { from: "any", to: "any" } : {}),
			...(clearRun ? { section: null, event: null } : {}),
		});
	};

	const changeView = (next: "table" | "dashboard") => {
		if (next === "table" && dashboardEvent !== null) {
			setLabels((current) => ({
				...current,
				sectionName: dashboardEvent.SectionName,
			}));
			setDatesPinned(false);
			setQuery({
				view: next,
				event: dashboardEvent.EventId,
				section: dashboardEvent.SectionId,
				from: "any",
				to: "any",
			});
			return;
		}

		setQuery({ view: next });
	};

	const generatePdf = async () => {
		setIsGeneratingPdf(true);

		try {
			const blob = await getInspectionDashboardPdf({
				startDate:
					dashboardEventId === null
						? (dashboardWindow?.startDate.toISOString() ?? null)
						: null,
				endDate:
					dashboardEventId === null
						? (dashboardWindow?.endDate.toISOString() ?? null)
						: null,
				eventId: dashboardEventId,
				periodLabel: dashboardLabel,
			});

			const url = URL.createObjectURL(blob);
			window.open(url, "_blank", "noopener,noreferrer");
			setTimeout(() => URL.revokeObjectURL(url), 10_000);
		} catch (pdfError) {
			setError((pdfError as Error).message);
		} finally {
			setIsGeneratingPdf(false);
		}
	};

	useEffect(() => {
		if (isOptionsError && optionsError) {
			setError(optionsError.message);
		}
		if (isReportError && reportError) {
			setError(reportError.message);
		}
	}, [isOptionsError, optionsError, isReportError, reportError]);

	return (
		<>
			<HelmetDexteel title="Inspections Report" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				sx={{
					alignContent: "flex-start",
					backgroundColor: pageBgColor,
					maxHeight: "calc(100vh - 200px)",
				}}
			>
				<Grid2
					size={{ md: 12, xs: 12 }}
					container
					alignItems="center"
					justifyContent="space-between"
				>
					<Typography variant="h5" fontWeight={600}>
						Inspections Report
					</Typography>
					<ToggleButtonGroup
						value={view}
						exclusive
						size="small"
						onChange={(_event, value) => value && changeView(value)}
						sx={{
							"& .MuiToggleButton-root": { px: 1.25, textTransform: "none" },
							"& .MuiToggleButton-root.Mui-selected": {
								bgcolor: "primary.main",
								color: "primary.contrastText",
								"&:hover": { bgcolor: "primary.dark" },
							},
						}}
					>
						<Tooltip title="Table view: sheets and their readings">
							<ToggleButton value="table">
								<TableChartOutlined fontSize="small" sx={{ mr: 0.5 }} />
								Table
							</ToggleButton>
						</Tooltip>
						<Tooltip title="Dashboard view: tiles and charts">
							<ToggleButton value="dashboard">
								<DashboardOutlined fontSize="small" sx={{ mr: 0.5 }} />
								Dashboard
							</ToggleButton>
						</Tooltip>
					</ToggleButtonGroup>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<InspectionsReportFilters
						view={view}
						filters={{
							...filters,
							eventId: isDashboard ? dashboardEventId : eventId,
							heatId,
						}}
						events={options?.events ?? []}
						heats={options?.heats ?? []}
						isLoadingOptions={isLoadingOptions}
						changedFilters={changedFilters}
						onChange={handleFilterChange}
						onRestore={restoreFilters}
						actions={
							isDashboard && (
								<ButtonWithLoading
									variant="outlined"
									size="small"
									startIcon={
										<PictureAsPdf sx={{ color: "error.main", fontSize: 18 }} />
									}
									isLoading={isGeneratingPdf}
									disabled={
										isLoading ||
										(dashboardEventId === null && dashboardWindow === null)
									}
									onClick={generatePdf}
									sx={{
										backgroundColor: "background.paper",
										borderColor: "divider",
										color: "text.secondary",
										fontSize: "0.8125rem",
										fontWeight: 500,
										textTransform: "none",
										whiteSpace: "nowrap",
										"&:hover": {
											backgroundColor: "action.hover",
											borderColor: "text.disabled",
										},
									}}
								>
									PDF
								</ButtonWithLoading>
							)
						}
					/>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }} pb={2}>
					{isDashboard ? (
						<InspectionsReportDashboard
							period={dashboardWindow}
							eventId={dashboardEventId}
							isLoadingEvents={filters.sectionId !== null && isLoadingOptions}
							periodLabel={dashboardLabel}
							onError={setError}
							onSectionSelect={(sectionId, sectionName) =>
								handleFilterChange({ sectionId, sectionName, eventId: null })
							}
							canSelectSection={(sectionId) =>
								!!options?.events.some((event) => event.SectionId === sectionId)
							}
						/>
					) : (
						<InspectionsReportTable
							sheets={report?.sheets ?? []}
							details={report?.details}
							filters={{ ...filters, eventId, heatId, heatName }}
							onError={setError}
							isLoading={isLoading}
							problemsOnly={filters.problemsOnly}
							onProblemsOnlyChange={(problemsOnly) =>
								handleFilterChange({ problemsOnly })
							}
							expandAll={expandAll}
							onExpandAllChange={setExpandAll}
						/>
					)}
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
